import type { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import { fetchPlacementData } from '@atlassian/post-office-placement-data/fetchPlacementData';
import type { usePlacementFetchingContext } from '@atlassian/post-office-placement-data/placement-fetcher-provider';
import { captureException } from '@sentry/react';

import type { useAnalytics } from '@townsquare/analytics';
import { RequestError } from '@townsquare/error-classes';

import type { PlacementMessages } from './types';

const REQUEST_TIMEOUT_MS = 10_000;
const SOURCE = 'post-office-shared-placement-messages';
const ANALYTICS_SUBJECT = 'postOfficeMessages';
const UNREPORTED_STATUS_CODES = new Set([401, 403]);

export type FetchDependencies = {
	postOfficeContext: ReturnType<typeof usePostOfficeContext>;
	fetcher: ReturnType<typeof usePlacementFetchingContext>;
	analytics: ReturnType<typeof useAnalytics>;
};

const isClientError = (statusCode: number | undefined): boolean =>
	statusCode !== undefined && statusCode >= 400 && statusCode < 500;

export const fetchMessages = async (
	placementId: string,
	{ postOfficeContext, fetcher, analytics }: FetchDependencies,
): Promise<PlacementMessages> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

	const reportFailure = (error: unknown): Error => {
		const reported = error instanceof Error ? error : new Error(String(error));
		const statusCode = reported instanceof RequestError ? reported.statusCode : undefined;
		if (!(statusCode && UNREPORTED_STATUS_CODES.has(statusCode))) {
			captureException(reported, {
				level: 'warning',
				tags: { source: SOURCE, placementId },
				extra: { statusCode },
			});
		}
		if (!isClientError(statusCode)) {
			void analytics.operational(ANALYTICS_SUBJECT, 'taskFail', { placementId });
		}
		return reported;
	};

	try {
		const response = await fetchPlacementData(placementId, '/', postOfficeContext, fetcher, {
			signal: controller.signal,
		});
		if (!response) {
			throw new Error('No response from the placement request');
		}
		if (!response.ok) {
			throw new RequestError(response.statusText, response.status);
		}
		const body: { messages?: unknown } = await response.json();
		if (!Array.isArray(body.messages)) {
			throw new Error('Placement response has no messages array');
		}
		void analytics.operational(ANALYTICS_SUBJECT, 'taskSuccess', { placementId });
		return body.messages;
	} catch (error) {
		throw reportFailure(error);
	} finally {
		clearTimeout(timer);
	}
};
